import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, Modal,
  TextInput, Dimensions, Animated, Platform, StatusBar,
  BackHandler, RefreshControl, Alert,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

// ─── AI SERVICE ───────────────────────────────────────────────────────────────
let aiService = null;
try { aiService = require('../../../services/aiService'); } catch {}


const { width: W } = Dimensions.get('window');

const BRAND = {
  blue: '#0e4e96', canary: '#fdb913', mint: '#19abb5',
  bg: '#f8fafc', surface: '#ffffff', text: '#0f172a', sub: '#475569',
  line: '#e2e8f0', success: '#10b981', warning: '#f59e0b',
  danger: '#ef4444', info: '#3b82f6', purple: '#8b5cf6', pink: '#ec4899',
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const EVENT_TYPES = {
  deadline:  { color: BRAND.danger,   icon: 'clock-alert',         label: 'Deadline'    },
  meeting:   { color: BRAND.blue,     icon: 'account-group',       label: 'Meeting'     },
  task:      { color: BRAND.purple,   icon: 'check-circle',        label: 'Task'        },
  payroll:   { color: BRAND.pink,     icon: 'currency-gbp',        label: 'Payroll'     },
  invoice:   { color: BRAND.warning,  icon: 'file-document',       label: 'Invoice'     },
  report:    { color: '#6366f1',      icon: 'file-chart',          label: 'Report'      },
  review:    { color: BRAND.success,  icon: 'star',                label: 'Review'      },
  delivery:  { color: BRAND.mint,     icon: 'truck',               label: 'Delivery'    },
};

// Events keyed by date string "YYYY-MM-DD"
const BASE_EVENTS = {
  '2025-01-27': [
    { id: 'E001', title: 'Board Meeting – Q4 Review', time: '09:00', duration: '2h', type: 'meeting', module: 'Management', desc: 'Quarterly board review. All directors required.', attendees: ['CEO', 'CFO', 'COO', 'CTO'] },
    { id: 'E002', title: 'Invoice Due – XYZ Corp', time: '17:00', duration: '–', type: 'invoice', module: 'Finance', desc: 'INV-2024-012 overdue. £8,500. Call client.', attendees: [] },
  ],
  '2025-01-28': [
    { id: 'E003', title: 'Sprint Planning – Mobile App', time: '10:00', duration: '3h', type: 'task', module: 'Projects', desc: 'Plan Q1 sprint for Mobile App v3. Dev team.', attendees: ['Anna Lee', 'Dev Team'] },
    { id: 'E004', title: 'Payroll Approval – January', time: '14:00', duration: '1h', type: 'payroll', module: 'HR', desc: 'Approve January payroll run before 31 Jan.', attendees: ['CFO', 'HR Manager'] },
  ],
  '2025-01-29': [
    { id: 'E005', title: 'Sales Team Weekly Standup', time: '09:30', duration: '30m', type: 'meeting', module: 'CRM', desc: 'Weekly pipeline review and forecasting.', attendees: ['Sales Team x8'] },
    { id: 'E006', title: 'Inventory Audit – Warehouse 1', time: '13:00', duration: '4h', type: 'task', module: 'Inventory', desc: 'Full stock count at Main Warehouse, London.', attendees: ['Ops Team'] },
  ],
  '2025-01-30': [
    { id: 'E007', title: 'Beta Industries – Deal Closing', time: '11:00', duration: '1h', type: 'meeting', module: 'CRM', desc: 'Final negotiation for £89k consulting deal.', attendees: ['Jane Doe', 'Account Manager'] },
    { id: 'E008', title: 'Alpha Initiative – Progress Review', time: '15:00', duration: '1.5h', type: 'review', module: 'Projects', desc: 'Review 75% completion milestone.', attendees: ['Sarah Johnson', 'Team Leads'] },
  ],
  '2025-01-31': [
    { id: 'E009', title: 'January Payroll Run', time: '08:00', duration: '–', type: 'payroll', module: 'HR', desc: 'Process January payroll. 245 employees. £924k.', attendees: ['HR Manager', 'Finance'] },
    { id: 'E010', title: 'Month-End Close – Finance', time: '17:00', duration: '–', type: 'deadline', module: 'Finance', desc: 'All invoices and journals must be posted.', attendees: ['Finance Team'] },
    { id: 'E011', title: 'P&L Report Generation', time: '18:00', duration: 'Auto', type: 'report', module: 'Finance', desc: 'Auto-scheduled monthly P&L report.', attendees: [] },
  ],
  '2025-02-01': [
    { id: 'E012', title: 'Gamma Integration – Go-Live', time: '09:00', duration: '–', type: 'deadline', module: 'Projects', desc: 'Final delivery deadline. 90% complete.', attendees: ['Emma Wilson', 'Client Team'] },
    { id: 'E013', title: 'New Hire Onboarding – Feb Batch', time: '10:00', duration: '4h', type: 'task', module: 'HR', desc: 'Onboard 4 new starters. IT setup and induction.', attendees: ['HR Manager', 'IT Team'] },
  ],
  '2025-02-03': [
    { id: 'E014', title: 'PO Delivery – Server Hardware', time: '09:00', duration: '–', type: 'delivery', module: 'Procurement', desc: 'Dell server hardware delivery. PO001.', attendees: ['IT Manager'] },
    { id: 'E015', title: 'Weekly Reporting – All Modules', time: '07:00', duration: 'Auto', type: 'report', module: 'System', desc: 'Auto-scheduled weekly module reports.', attendees: [] },
  ],
  '2025-02-05': [
    { id: 'E016', title: 'Stakeholder Presentation – Delta CRM', time: '14:00', duration: '2h', type: 'meeting', module: 'Projects', desc: 'Present Delta CRM upgrade plan to stakeholders.', attendees: ['Sarah Johnson', 'Stakeholders'] },
    { id: 'E017', title: 'Q1 Sales Target Review', time: '16:00', duration: '1h', type: 'review', module: 'CRM', desc: 'Review pipeline vs Q1 £3.2M target.', attendees: ['Sales Director', 'Team Leads'] },
  ],
  '2025-02-10': [
    { id: 'E018', title: 'Performance Appraisals Begin', time: '09:00', duration: '–', type: 'review', module: 'HR', desc: 'Q1 performance review cycle starts. 245 employees.', attendees: ['All Managers'] },
    { id: 'E019', title: 'PO Delivery – Warehouse Shelving', time: '08:00', duration: '–', type: 'delivery', module: 'Procurement', desc: 'Racking Systems delivery. £15k order.', attendees: ['Ops Team'] },
  ],
  '2025-02-15': [
    { id: 'E020', title: 'Alpha Initiative Deadline', time: '18:00', duration: '–', type: 'deadline', module: 'Projects', desc: 'Alpha Initiative final deliverable. £125k project.', attendees: ['Sarah Johnson', 'PM Team'] },
  ],
};

const today = new Date(2025, 0, 27); // Fixed mock date

const ERPCalendarScreen = ({ navigation }) => {
  const [viewMode, setViewMode] = useState('month'); // month | week | agenda
  const [currentDate, setCurrentDate] = useState(new Date(today));
  const [selectedDate, setSelectedDate] = useState(new Date(today));
  const [events, setEvents] = useState(BASE_EVENTS);
  const [showEventDetail, setShowEventDetail] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [form, setForm] = useState({ title: '', type: 'meeting', time: '09:00', duration: '1h', module: 'General', desc: '', attendees: '' });
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(fadeAnim, { toValue: 1, duration: 500, useNativeDriver: true }).start();
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      if (showEventDetail || showAddEvent) { setShowEventDetail(false); setShowAddEvent(false); return true; }
      return false;
    });
    return () => sub.remove();
  }, [showEventDetail, showAddEvent]);

  const fmtDateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const selectedKey = fmtDateKey(selectedDate);
  const selectedEvents = useMemo(() => {
    let evts = events[selectedKey] || [];
    if (filterType !== 'all') evts = evts.filter(e => e.type === filterType);
    return evts;
  }, [events, selectedKey, filterType]);

  const allUpcoming = useMemo(() => {
    const all = [];
    Object.entries(events).forEach(([date, evts]) => {
      evts.forEach(e => all.push({ ...e, date }));
    });
    all.sort((a, b) => a.date.localeCompare(b.date));
    if (filterType !== 'all') return all.filter(e => e.type === filterType);
    return all;
  }, [events, filterType]);

  const getDaysInMonth = (d) => {
    const year = d.getFullYear(), month = d.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  };

  const hasEvents = (day) => {
    if (!day) return [];
    const key = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events[key] || [];
  };

  const isToday = (day) => {
    if (!day) return false;
    return day === today.getDate() && currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear();
  };

  const isSelected = (day) => {
    if (!day) return false;
    return day === selectedDate.getDate() && currentDate.getMonth() === selectedDate.getMonth() && currentDate.getFullYear() === selectedDate.getFullYear();
  };

  const selectDay = (day) => {
    if (!day) return;
    setSelectedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
  };

  const prevMonth = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  const addEvent = () => {
    if (!form.title) { Alert.alert('Required', 'Please enter an event title.'); return; }
    const key = selectedKey;
    const newEvt = { id: `E${Date.now()}`, title: form.title, time: form.time, duration: form.duration, type: form.type, module: form.module, desc: form.desc, attendees: form.attendees ? form.attendees.split(',').map(s => s.trim()) : [] };
    setEvents(prev => ({ ...prev, [key]: [...(prev[key] || []), newEvt] }));
    setForm({ title: '', type: 'meeting', time: '09:00', duration: '1h', module: 'General', desc: '', attendees: '' });
    setShowAddEvent(false);
  };

  const deleteEvent = (evt) => {
    Alert.alert('Delete Event', `Remove "${evt.title}"?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => {
        const key = selectedKey;
        setEvents(prev => ({ ...prev, [key]: (prev[key] || []).filter(e => e.id !== evt.id) }));
        setShowEventDetail(false);
      }},
    ]);
  };

  const renderEventChip = (evt, compact = false) => {
    const cfg = EVENT_TYPES[evt.type] || EVENT_TYPES.task;
    return (
      <TouchableOpacity key={evt.id} style={[cs.eventChip, { backgroundColor: cfg.color + '18', borderColor: cfg.color + '40', borderLeftColor: cfg.color, borderLeftWidth: 3 }]} onPress={() => { setSelectedEvent(evt); setShowEventDetail(true); }} activeOpacity={0.75}>
        <View style={{ flex: 1 }}>
          <Text style={[cs.evtTitle, { color: BRAND.text }]} numberOfLines={1}>{evt.title}</Text>
          {!compact && (
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 3, alignItems: 'center' }}>
              <MaterialCommunityIcons name={cfg.icon} size={11} color={cfg.color} />
              <Text style={[cs.evtMeta, { color: BRAND.sub }]}>{evt.time}{evt.duration !== '–' ? ` · ${evt.duration}` : ''}</Text>
              <View style={[cs.modBadge, { backgroundColor: cfg.color + '15' }]}><Text style={[cs.modBadgeText, { color: cfg.color }]}>{evt.module}</Text></View>
            </View>
          )}
        </View>
        {compact && <View style={[cs.evtDot, { backgroundColor: cfg.color }]} />}
      </TouchableOpacity>
    );
  };

  const renderMonthView = () => {
    const days = getDaysInMonth(currentDate);
    const cellW = (W - 32) / 7;
    return (
      <ScrollView showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); setTimeout(() => setRefreshing(false), 600); }} tintColor={BRAND.blue} />}>
        {/* Day headers */}
        <View style={{ flexDirection: 'row', paddingHorizontal: 16, paddingTop: 12 }}>
          {DAYS.map(d => <Text key={d} style={[cs.dayHeader, { width: cellW, color: d === 'Sun' || d === 'Sat' ? BRAND.danger + '80' : BRAND.sub }]}>{d}</Text>)}
        </View>
        {/* Calendar grid */}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', paddingHorizontal: 16, paddingBottom: 8 }}>
          {days.map((day, i) => {
            const dayEvts = hasEvents(day);
            const sel = isSelected(day);
            const tod = isToday(day);
            return (
              <TouchableOpacity key={i} style={[cs.dayCell, { width: cellW, height: cellW * 1.1 }]} onPress={() => selectDay(day)} activeOpacity={day ? 0.7 : 1}>
                {day && (
                  <>
                    <View style={[cs.dayNum, tod && cs.todayNum, sel && !tod && cs.selNum]}>
                      <Text style={[cs.dayNumText, { color: tod ? '#fff' : sel ? BRAND.blue : day && (i % 7 === 0 || i % 7 === 6) ? BRAND.danger + '80' : BRAND.text }]}>{day}</Text>
                    </View>
                    {dayEvts.length > 0 && (
                      <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 2, marginTop: 2 }}>
                        {dayEvts.slice(0, 3).map((e, ei) => {
                          const cfg = EVENT_TYPES[e.type] || EVENT_TYPES.task;
                          return <View key={ei} style={[cs.eventDot, { backgroundColor: cfg.color }]} />;
                        })}
                        {dayEvts.length > 3 && <Text style={{ fontSize: 8, color: BRAND.sub }}>+{dayEvts.length - 3}</Text>}
                      </View>
                    )}
                  </>
                )}
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Selected day events */}
        <View style={[cs.dayEventsPanel, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <View>
              <Text style={[cs.dayEventsPanelTitle, { color: BRAND.text }]}>
                {selectedDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
              </Text>
              <Text style={[cs.dayEventsPanelSub, { color: BRAND.sub }]}>{selectedEvents.length} event{selectedEvents.length !== 1 ? 's' : ''}</Text>
            </View>
            <TouchableOpacity style={[cs.addBtn, { backgroundColor: BRAND.blue }]} onPress={() => setShowAddEvent(true)}>
              <MaterialCommunityIcons name="plus" size={16} color="#fff" />
            </TouchableOpacity>
          </View>
          {selectedEvents.length === 0 ? (
            <View style={cs.noEvents}>
              <MaterialCommunityIcons name="calendar-blank" size={36} color={BRAND.line} />
              <Text style={[cs.noEventsText, { color: BRAND.sub }]}>No events — tap + to add one</Text>
            </View>
          ) : (
            selectedEvents.map(e => renderEventChip(e))
          )}
        </View>
        <View style={{ height: 30 }} />
      </ScrollView>
    );
  };

  const renderWeekView = () => {
    const startOfWeek = new Date(selectedDate);
    startOfWeek.setDate(selectedDate.getDate() - selectedDate.getDay());
    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      return d;
    });
    return (
      <ScrollView showsVerticalScrollIndicator={false}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ borderBottomWidth: 1, borderBottomColor: BRAND.line }}>
          <View style={{ flexDirection: 'row', paddingHorizontal: 16, paddingVertical: 10 }}>
            {weekDays.map((d, i) => {
              const key = fmtDateKey(d);
              const evts = events[key] || [];
              const isTod = fmtDateKey(d) === fmtDateKey(today);
              const isSel = fmtDateKey(d) === selectedKey;
              return (
                <TouchableOpacity key={i} style={[cs.weekDayCol, isSel && { backgroundColor: BRAND.blue + '10', borderRadius: 12 }]} onPress={() => setSelectedDate(d)}>
                  <Text style={[cs.weekDayName, { color: BRAND.sub }]}>{DAYS[d.getDay()]}</Text>
                  <View style={[cs.weekDayNum, isTod && { backgroundColor: BRAND.blue }]}><Text style={[cs.weekDayNumText, { color: isTod ? '#fff' : BRAND.text }]}>{d.getDate()}</Text></View>
                  {evts.length > 0 && <View style={[cs.weekEvtCount, { backgroundColor: BRAND.blue }]}><Text style={{ color: '#fff', fontSize: 9, fontWeight: '700' }}>{evts.length}</Text></View>}
                </TouchableOpacity>
              );
            })}
          </View>
        </ScrollView>
        <View style={{ padding: 16 }}>
          <Text style={[cs.dayEventsPanelTitle, { color: BRAND.text, marginBottom: 12 }]}>
            {selectedDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </Text>
          {selectedEvents.length === 0 ? (
            <View style={cs.noEvents}>
              <MaterialCommunityIcons name="calendar-blank" size={36} color={BRAND.line} />
              <Text style={[cs.noEventsText, { color: BRAND.sub }]}>No events today</Text>
            </View>
          ) : (
            selectedEvents.map(e => renderEventChip(e))
          )}
          <TouchableOpacity style={[cs.addEventBtn, { borderColor: BRAND.blue + '40', backgroundColor: BRAND.blue + '08' }]} onPress={() => setShowAddEvent(true)}>
            <MaterialCommunityIcons name="plus" size={16} color={BRAND.blue} />
            <Text style={[cs.addEventBtnText, { color: BRAND.blue }]}>Add Event</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    );
  };

  const renderAgendaView = () => (
    <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); setTimeout(() => setRefreshing(false), 600); }} tintColor={BRAND.blue} />}>
      {/* Filter chips */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingLeft: 16, paddingVertical: 10, maxHeight: 52 }}>
        {[{ id: 'all', label: 'All', color: BRAND.blue }, ...Object.entries(EVENT_TYPES).map(([id, cfg]) => ({ id, label: cfg.label, color: cfg.color }))].map(f => (
          <TouchableOpacity key={f.id} style={[cs.filterChip, filterType === f.id && { backgroundColor: f.color, borderColor: f.color }, { borderColor: BRAND.line }]} onPress={() => setFilterType(f.id)}>
            <Text style={[cs.filterChipText, { color: filterType === f.id ? '#fff' : BRAND.sub }]}>{f.label}</Text>
          </TouchableOpacity>
        ))}
        <View style={{ width: 16 }} />
      </ScrollView>

      <View style={{ paddingHorizontal: 16, paddingBottom: 80 }}>
        {allUpcoming.length === 0 ? (
          <View style={[cs.noEvents, { paddingVertical: 60 }]}>
            <MaterialCommunityIcons name="calendar-search" size={52} color={BRAND.line} />
            <Text style={[cs.noEventsText, { color: BRAND.sub, marginTop: 12 }]}>No upcoming events</Text>
          </View>
        ) : (
          (() => {
            const grouped = {};
            allUpcoming.forEach(e => { if (!grouped[e.date]) grouped[e.date] = []; grouped[e.date].push(e); });
            return Object.entries(grouped).map(([date, evts]) => {
              const d = new Date(date + 'T00:00:00');
              const isT = date === fmtDateKey(today);
              return (
                <View key={date} style={{ marginBottom: 16 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <View style={[cs.agendaDateBubble, isT && { backgroundColor: BRAND.blue }]}>
                      <Text style={[cs.agendaDateNum, { color: isT ? '#fff' : BRAND.text }]}>{d.getDate()}</Text>
                    </View>
                    <View>
                      <Text style={[cs.agendaDateLabel, { color: isT ? BRAND.blue : BRAND.text, fontWeight: isT ? '800' : '600' }]}>
                        {isT ? 'Today' : d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </Text>
                      <Text style={[cs.agendaEvtCount, { color: BRAND.sub }]}>{evts.length} event{evts.length !== 1 ? 's' : ''}</Text>
                    </View>
                  </View>
                  {evts.map(e => renderEventChip(e))}
                </View>
              );
            });
          })()
        )}
      </View>
    </ScrollView>
  );

  return (
    <View style={{ flex: 1, backgroundColor: BRAND.bg }}>
      <StatusBar barStyle="light-content" backgroundColor={BRAND.blue} />
      <LinearGradient colors={[BRAND.blue, '#163b6c']} style={cs.header}>
        <View style={cs.headerRow}>
          <TouchableOpacity style={cs.backBtn} onPress={() => navigation?.goBack()}><MaterialCommunityIcons name="arrow-left" size={22} color="#fff" /></TouchableOpacity>
          {/* Month nav */}
          <TouchableOpacity onPress={prevMonth}><MaterialCommunityIcons name="chevron-left" size={22} color="rgba(255,255,255,0.8)" /></TouchableOpacity>
          <TouchableOpacity onPress={() => setCurrentDate(new Date(today))} style={{ flex: 1, alignItems: 'center' }}>
            <Text style={cs.headerMonth}>{MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}</Text>
            <Text style={cs.headerSub}>{Object.values(events).flat().length} events total</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={nextMonth}><MaterialCommunityIcons name="chevron-right" size={22} color="rgba(255,255,255,0.8)" /></TouchableOpacity>
          <TouchableOpacity style={cs.headerBtn} onPress={() => setShowAddEvent(true)}><MaterialCommunityIcons name="plus" size={20} color="#fff" /></TouchableOpacity>
        </View>
        {/* View mode selector */}
        <View style={cs.viewModeRow}>
          {[{ id: 'month', label: 'Month', icon: 'calendar-month' }, { id: 'week', label: 'Week', icon: 'calendar-week' }, { id: 'agenda', label: 'Agenda', icon: 'format-list-text' }].map(v => (
            <TouchableOpacity key={v.id} style={[cs.viewModeBtn, viewMode === v.id && cs.viewModeBtnActive]} onPress={() => setViewMode(v.id)}>
              <MaterialCommunityIcons name={v.icon} size={13} color={viewMode === v.id ? BRAND.blue : 'rgba(255,255,255,0.75)'} />
              <Text style={[cs.viewModeText, viewMode === v.id && { color: BRAND.blue }]}>{v.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </LinearGradient>

      <Animated.View style={{ flex: 1, opacity: fadeAnim }}>
        {viewMode === 'month' && renderMonthView()}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'agenda' && renderAgendaView()}
      </Animated.View>

      {/* EVENT DETAIL MODAL */}
      <Modal visible={showEventDetail} animationType="slide" presentationStyle="formSheet" onRequestClose={() => setShowEventDetail(false)}>
        {selectedEvent && (() => {
          const cfg = EVENT_TYPES[selectedEvent.type] || EVENT_TYPES.task;
          return (
            <View style={{ flex: 1, backgroundColor: BRAND.bg }}>
              <LinearGradient colors={[cfg.color, cfg.color + 'aa']} style={{ flexDirection: 'row', alignItems: 'center', padding: 20, gap: 12 }}>
                <TouchableOpacity style={cs.modalClose} onPress={() => setShowEventDetail(false)}><MaterialCommunityIcons name="close" size={22} color="#fff" /></TouchableOpacity>
                <View style={{ flex: 1 }}>
                  <Text style={{ color: '#fff', fontSize: 16, fontWeight: '800' }} numberOfLines={2}>{selectedEvent.title}</Text>
                  <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11, marginTop: 2 }}>{cfg.label} · {selectedEvent.module}</Text>
                </View>
              </LinearGradient>
              <ScrollView style={{ flex: 1, padding: 20 }}>
                <View style={[cs.evtDetailCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
                  {[
                    { l: 'Date', v: selectedDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }), i: 'calendar' },
                    { l: 'Time', v: selectedEvent.time, i: 'clock' },
                    { l: 'Duration', v: selectedEvent.duration, i: 'timer' },
                    { l: 'Module', v: selectedEvent.module, i: 'apps' },
                    { l: 'Type', v: cfg.label, i: cfg.icon },
                  ].map((f, i) => (
                    <View key={i} style={[cs.evtDetailRow, { borderBottomColor: BRAND.line, borderBottomWidth: i < 4 ? 1 : 0 }]}>
                      <MaterialCommunityIcons name={f.i} size={14} color={BRAND.sub} style={{ marginRight: 8, width: 18 }} />
                      <Text style={[cs.evtDetailLabel, { color: BRAND.sub }]}>{f.l}</Text>
                      <Text style={[cs.evtDetailValue, { color: BRAND.text }]}>{f.v}</Text>
                    </View>
                  ))}
                </View>
                {selectedEvent.desc && (
                  <View style={[cs.evtDetailCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
                    <Text style={[cs.evtDetailSectionTitle, { color: BRAND.sub }]}>DESCRIPTION</Text>
                    <Text style={[cs.evtDesc, { color: BRAND.text }]}>{selectedEvent.desc}</Text>
                  </View>
                )}
                {selectedEvent.attendees?.length > 0 && (
                  <View style={[cs.evtDetailCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
                    <Text style={[cs.evtDetailSectionTitle, { color: BRAND.sub }]}>ATTENDEES ({selectedEvent.attendees.length})</Text>
                    {selectedEvent.attendees.map((a, ai) => (
                      <View key={ai} style={[cs.attendeeRow, { borderBottomColor: BRAND.line, borderBottomWidth: ai < selectedEvent.attendees.length - 1 ? 1 : 0 }]}>
                        <View style={[cs.attendeeAvatar, { backgroundColor: cfg.color + '20' }]}><Text style={[cs.attendeeAvatarText, { color: cfg.color }]}>{a.charAt(0)}</Text></View>
                        <Text style={[cs.attendeeName, { color: BRAND.text }]}>{a}</Text>
                      </View>
                    ))}
                  </View>
                )}
                <TouchableOpacity style={[cs.deleteBtn, { borderColor: BRAND.danger + '40', backgroundColor: BRAND.danger + '10' }]} onPress={() => deleteEvent(selectedEvent)}>
                  <MaterialCommunityIcons name="delete" size={16} color={BRAND.danger} />
                  <Text style={[cs.deleteBtnText, { color: BRAND.danger }]}>Delete Event</Text>
                </TouchableOpacity>
              </ScrollView>
            </View>
          );
        })()}
      </Modal>

      {/* ADD EVENT MODAL */}
      <Modal visible={showAddEvent} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAddEvent(false)}>
        <View style={{ flex: 1, backgroundColor: BRAND.bg }}>
          <LinearGradient colors={[BRAND.blue, '#163b6c']} style={{ flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12, paddingTop: Platform.OS === 'android' ? (StatusBar.currentHeight || 24) + 16 : 16 }}>
            <TouchableOpacity style={cs.modalClose} onPress={() => setShowAddEvent(false)}><MaterialCommunityIcons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={{ flex: 1, color: '#fff', fontSize: 16, fontWeight: '700' }}>Add Event — {selectedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</Text>
          </LinearGradient>
          <ScrollView style={{ flex: 1, padding: 16 }}>
            {[
              { k: 'title', l: 'Event Title *', i: 'calendar', ph: 'e.g. Board Meeting' },
              { k: 'time', l: 'Time', i: 'clock', ph: '09:00' },
              { k: 'duration', l: 'Duration', i: 'timer', ph: '1h' },
              { k: 'module', l: 'Module', i: 'apps', ph: 'e.g. Finance, CRM, HR' },
              { k: 'desc', l: 'Description', i: 'text', ph: 'Event details…', multi: true },
              { k: 'attendees', l: 'Attendees (comma-separated)', i: 'account-group', ph: 'e.g. John Smith, Jane Doe' },
            ].map(f => (
              <View key={f.k} style={{ marginBottom: 14 }}>
                <Text style={[cs.fieldLabel, { color: BRAND.text }]}>{f.l}</Text>
                <View style={[cs.fieldBox, { backgroundColor: BRAND.surface, borderColor: form[f.k] ? BRAND.blue : BRAND.line }]}>
                  <MaterialCommunityIcons name={f.i} size={16} color={BRAND.sub} />
                  <TextInput value={form[f.k]} onChangeText={v => setForm(p => ({ ...p, [f.k]: v }))} placeholder={f.ph} placeholderTextColor={BRAND.sub} style={[cs.fieldInput, { color: BRAND.text }]} multiline={f.multi} numberOfLines={f.multi ? 3 : 1} />
                </View>
              </View>
            ))}
            <Text style={[cs.fieldLabel, { color: BRAND.text }]}>Event Type</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
              {Object.entries(EVENT_TYPES).map(([id, cfg]) => (
                <TouchableOpacity key={id} style={[cs.typePill, form.type === id && { backgroundColor: cfg.color, borderColor: cfg.color }, { borderColor: BRAND.line }]} onPress={() => setForm(p => ({ ...p, type: id }))}>
                  <MaterialCommunityIcons name={cfg.icon} size={12} color={form.type === id ? '#fff' : cfg.color} />
                  <Text style={[cs.typePillText, { color: form.type === id ? '#fff' : BRAND.sub }]}>{cfg.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <TouchableOpacity style={[cs.saveBtn, { backgroundColor: BRAND.blue }]} onPress={addEvent}>
              <MaterialCommunityIcons name="check" size={18} color="#fff" />
              <Text style={cs.saveBtnText}>Save Event</Text>
            </TouchableOpacity>
            <View style={{ height: 40 }} />
          </ScrollView>
        </View>
      </Modal>
    </View>
  );
};

const cs = StyleSheet.create({
  header: { paddingTop: Platform.OS === 'android' ? (StatusBar.currentHeight || 24) : 0, paddingHorizontal: 16, paddingBottom: 0 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 12 },
  backBtn: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  headerMonth: { color: '#fff', fontSize: 16, fontWeight: '800', textAlign: 'center' },
  headerSub: { color: 'rgba(255,255,255,0.75)', fontSize: 10, textAlign: 'center', marginTop: 1 },
  headerBtn: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  viewModeRow: { flexDirection: 'row', gap: 6, paddingBottom: 10, paddingTop: 4 },
  viewModeBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5, paddingVertical: 7, borderRadius: 16 },
  viewModeBtnActive: { backgroundColor: '#fff' },
  viewModeText: { color: 'rgba(255,255,255,0.8)', fontSize: 12, fontWeight: '600' },
  dayHeader: { textAlign: 'center', fontSize: 11, fontWeight: '700', paddingVertical: 4 },
  dayCell: { alignItems: 'center', paddingVertical: 4 },
  dayNum: { width: 30, height: 30, borderRadius: 15, justifyContent: 'center', alignItems: 'center' },
  todayNum: { backgroundColor: BRAND.blue },
  selNum: { borderWidth: 2, borderColor: BRAND.blue },
  dayNumText: { fontSize: 13, fontWeight: '600' },
  eventDot: { width: 5, height: 5, borderRadius: 2.5 },
  dayEventsPanel: { marginHorizontal: 16, borderRadius: 18, padding: 16, borderWidth: 1, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 6 },
  dayEventsPanelTitle: { fontSize: 14, fontWeight: '700' },
  dayEventsPanelSub: { fontSize: 11, marginTop: 2 },
  addBtn: { width: 32, height: 32, borderRadius: 16, justifyContent: 'center', alignItems: 'center' },
  noEvents: { alignItems: 'center', paddingVertical: 24 },
  noEventsText: { fontSize: 13, marginTop: 8 },
  eventChip: { flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, borderRadius: 12, borderWidth: 1, marginBottom: 8, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3 },
  evtTitle: { fontSize: 13, fontWeight: '700' },
  evtMeta: { fontSize: 11 },
  evtDot: { width: 8, height: 8, borderRadius: 4 },
  modBadge: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 6 },
  modBadgeText: { fontSize: 9, fontWeight: '700' },
  weekDayCol: { width: (W - 32) / 7, alignItems: 'center', paddingVertical: 6, position: 'relative' },
  weekDayName: { fontSize: 10, fontWeight: '600', marginBottom: 4 },
  weekDayNum: { width: 30, height: 30, borderRadius: 15, justifyContent: 'center', alignItems: 'center' },
  weekDayNumText: { fontSize: 14, fontWeight: '700' },
  weekEvtCount: { width: 16, height: 16, borderRadius: 8, justifyContent: 'center', alignItems: 'center', position: 'absolute', top: 2, right: 2 },
  addEventBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 14, borderRadius: 14, borderWidth: 1, marginTop: 8 },
  addEventBtnText: { fontSize: 14, fontWeight: '700' },
  filterChip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 14, borderWidth: 1, marginRight: 8 },
  filterChipText: { fontSize: 11, fontWeight: '600' },
  agendaDateBubble: { width: 38, height: 38, borderRadius: 19, backgroundColor: BRAND.line, justifyContent: 'center', alignItems: 'center' },
  agendaDateNum: { fontSize: 16, fontWeight: '800' },
  agendaDateLabel: { fontSize: 13 },
  agendaEvtCount: { fontSize: 11, marginTop: 1 },
  modalClose: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  evtDetailCard: { borderRadius: 16, borderWidth: 1, overflow: 'hidden', marginBottom: 14, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  evtDetailRow: { flexDirection: 'row', alignItems: 'center', padding: 13 },
  evtDetailLabel: { flex: 1, fontSize: 12 },
  evtDetailValue: { fontSize: 13, fontWeight: '600' },
  evtDetailSectionTitle: { fontSize: 10, fontWeight: '700', letterSpacing: 0.8, textTransform: 'uppercase', margin: 14, marginBottom: 8 },
  evtDesc: { fontSize: 13, lineHeight: 20, paddingHorizontal: 14, paddingBottom: 14 },
  attendeeRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12 },
  attendeeAvatar: { width: 32, height: 32, borderRadius: 16, justifyContent: 'center', alignItems: 'center' },
  attendeeAvatarText: { fontSize: 12, fontWeight: '800' },
  attendeeName: { fontSize: 13, fontWeight: '600' },
  deleteBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 14, borderRadius: 14, borderWidth: 1, marginBottom: 30 },
  deleteBtnText: { fontSize: 14, fontWeight: '700' },
  fieldLabel: { fontSize: 13, fontWeight: '600', marginBottom: 6 },
  fieldBox: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 12, borderWidth: 1 },
  fieldInput: { flex: 1, fontSize: 14 },
  typePill: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 11, paddingVertical: 6, borderRadius: 14, borderWidth: 1 },
  typePillText: { fontSize: 11, fontWeight: '600' },
  saveBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 16, borderRadius: 16 },
  saveBtnText: { color: '#fff', fontSize: 16, fontWeight: '700' },
});

export default ERPCalendarScreen;